import { acceptHMRUpdate, defineStore } from 'pinia'
import { api } from '@/api/client'
import { num } from '@/utils/format'

/**
 * 后端快照 store。
 *
 * 只做取数与派生，不做业务判定 —— 风控状态机在 useRiskEngine 里，
 * 盘前计划在 plan store 里，各管一摊。
 */
export const useConsoleStore = defineStore('console', {
  state: () => ({
    connected: false,
    error: '',
    lastAt: 0,
    snap: null,

    // 行情历史点位，供图表使用
    points: [],
    liveSeeded: false,

    polling: null,

    // 可切换的交易所清单。单独拉取而不塞进快照：
    // 它几乎不变，没必要跟着 2 秒一次的轮询反复传。
    exchanges: [],
    switching: false,

    // 非交易账户。慢接口，进页面拉一次即可
    accounts: null,
  }),

  getters: {
    account: (s) => s.snap?.account ?? null,
    ledger: (s) => s.snap?.pnl ?? null,
    market: (s) => s.snap?.market ?? null,
    system: (s) => s.snap?.system ?? null,

    symbol: (s) => s.snap?.market?.symbol ?? 'BTC/USDT',
    baseAsset() {
      return this.symbol.split('/')[0]
    },

    price: (s) => num(s.snap?.market?.last),
    bid: (s) => num(s.snap?.market?.bid),
    ask: (s) => num(s.snap?.market?.ask),
    /** 价差。无买盘时后端给 null，这里保持 null —— 显示成 0.00 bps
     *  会让人以为盘口好得不得了，实际是一边根本没有挂单。 */
    spreadBps: (s) => num(s.snap?.market?.spread_bps),
    spreadOk: (s) => num(s.snap?.market?.spread_bps) !== null,
    mdAgeMs: (s) => s.snap?.market?.market_data_age_ms ?? Number.MAX_SAFE_INTEGER,
    /** 距最近一次收到任何帧（含心跳）的毫秒数 —— 链路是否还活着 */
    linkAgeMs: (s) => s.snap?.market?.link_age_ms ?? Number.MAX_SAFE_INTEGER,
    /** 推流自身的健康判定：链路断了、或（生产档案下）行情长时间不动 */
    streamHealthy: (s) => !!s.snap?.market?.stream_healthy,
    streamReasons: (s) => s.snap?.market?.stream_reasons ?? [],

    navUsd: (s) => num(s.snap?.account?.nav_usd),
    /** 余额一次都没取到时净值不可用 —— 不能把取数失败显示成 $0.00 */
    navAvailable: (s) => s.snap?.account?.nav_available !== false,
    navNote: (s) => s.snap?.account?.nav_note ?? '',
    availableUsd: (s) => num(s.snap?.account?.available_usd),
    positions: (s) => s.snap?.account?.positions ?? [],
    unpricedAssets: (s) => s.snap?.account?.unpriced_assets ?? [],
    /** 计入了净值、但折美元那一步不可信的资产 */
    wideFxAssets: (s) => s.snap?.account?.wide_fx_assets ?? [],
    /** 主交易账户之外的账户：钱在那里但下不了单。按需加载，不进轮询 */
    otherAccounts: (s) => s.accounts?.other_accounts ?? [],
    valuationTime: (s) => s.snap?.account?.valuation_time ?? 0,

    openOrders: (s) => s.snap?.orders?.open_orders ?? [],

    /** 后端持有的风控运行状态。刷新页面、换浏览器都是同一份。 */
    riskState: (s) => s.snap?.risk ?? null,

    // --- 账本派生 ---
    avgEntryPrice: (s) => num(s.snap?.pnl?.avg_entry_price),
    avgCostWithFee: (s) => num(s.snap?.pnl?.avg_cost_with_fee),
    costAmount: (s) => num(s.snap?.pnl?.cost_amount) ?? 0,
    buyFeeTotal: (s) => num(s.snap?.pnl?.buy_fee_total) ?? 0,
    realizedPnlNet: (s) => num(s.snap?.pnl?.realized_pnl_net) ?? 0,
    rawUnrealized: (s) => num(s.snap?.pnl?.unrealized_pnl_net),
    rawDailyPnl: (s) => num(s.snap?.pnl?.daily_pnl_net),

    // --- 账户级当日盈亏（跨交易所累加）---
    //
    // 三条亏损线原本只认当前这一家。切换交易所会重建账本、当日盈亏归零，
    // 于是"在 A 亏到快触线、切到 B 再亏一轮"完全不会被拦。
    rawAccountDailyPnl: (s) => num(s.snap?.account_pnl?.total),
    accountPnlComplete: (s) => !!s.snap?.account_pnl?.complete,
    accountPnlParts: (s) => s.snap?.account_pnl?.parts ?? [],
    accountPnlUnknown: (s) => s.snap?.account_pnl?.unknown ?? [],
    latchedExchanges: (s) => s.snap?.account_pnl?.latched ?? [],

    // --- 跨日闸门 ---
    //
    // 三条亏损线每天重置，管不了"每天单看都合规、一周下来失血"。
    // 前两道是急停（须人工书面确认），第三道是减速（回补自动恢复）。
    crossDay: (s) => s.snap?.cross_day ?? {},
    gateBlocksBuy: (s) => !!s.snap?.cross_day?.block_new_buy,
    gateReasons: (s) => s.snap?.cross_day?.reasons ?? [],
    ladderFactor: (s) => num(s.snap?.cross_day?.ladder_factor) ?? 1,

    // --- 保底棘轮 ---
    //
    // 与三条亏损线是两套独立的东西：那个管"今天别亏太多"，
    // 这个管"赚到的别还回去"。收工属止盈离场，不进风控状态机。
    ratchet: (s) => s.snap?.ratchet ?? {},
    ratchetArmed: (s) => !!s.snap?.ratchet?.armed,
    ratchetClosed: (s) => !!s.snap?.ratchet?.closed,
    ratchetPeak: (s) => num(s.snap?.ratchet?.peak),
    ratchetFloor: (s) => num(s.snap?.ratchet?.floor),
    ratchetHeadroom: (s) => num(s.snap?.ratchet?.headroom),
    ratchetReason: (s) => s.snap?.ratchet?.reason ?? '',
    totalPnlNet: (s) => num(s.snap?.pnl?.total_pnl_net),
    dayBaseline: (s) => num(s.snap?.pnl?.day_baseline),
    dayComplete: (s) => !!s.snap?.pnl?.day_complete,
    grossValue: (s) => num(s.snap?.pnl?.gross_value),
    estSellFee: (s) => num(s.snap?.pnl?.estimated_sell_fee),
    estSlippage: (s) => num(s.snap?.pnl?.estimated_slippage),
    untrackedQty: (s) => num(s.snap?.pnl?.untracked_qty) ?? 0,
    unfillableQty: (s) => num(s.snap?.pnl?.unfillable_qty) ?? 0,
    openingDeclared: (s) => !!s.snap?.pnl?.opening_declared,
    fillsApplied: (s) => s.snap?.pnl?.fills_applied ?? 0,
    effSellFeeRate: (s) => num(s.snap?.pnl?.effective_sell_fee_rate),
    ledgerNotes: (s) => s.snap?.pnl?.notes ?? [],

    /**
     * 净盈亏是否可信。
     *
     * 三种"不可信"：
     *   1. 后端算不出来（缺盘口或缺费率）→ 返回 null
     *   2. 账本持仓少于交易所 → 报出来的数是**残缺**的。
     *      这种比第 1 种更危险，因为它长得像个正常数字。
     *   3. 今日基线还没捕获 → 当日盈亏无从谈起。
     * 三种都不能喂给风险线。
     */
    pnlTrustworthy() {
      return (
        this.rawUnrealized !== null &&
        this.untrackedQty <= 0 &&
        this.rawDailyPnl !== null
      )
    },

    unrealizedPnlNet() {
      return this.rawUnrealized ?? 0
    },

    /**
     * 当日净盈亏 —— **直接取后端算好的值，不在这里重算**。
     *
     * 这里原来写的是 `已实现 + 浮动`。那个数不带"日"的概念：账本只在
     * 进程启动、声明期初、切换交易所时重建，从不日切，所以它累计的是
     * 账本开始以来的全部。后果是反的方向 —— 周一赚 $3,000、周五亏
     * $2,500，这个数显示 +$500，三条风险线一个都不会响，
     * **过去的盈利掩盖了今天的亏损**。
     *
     * 后端现在按「总盈亏 − 今日开盘基线」算，前端照抄即可。
     * 两处各算各的迟早会打架，这是断流判据上已经吃过的亏。
     */
    dailyPnlNet() {
      return this.rawDailyPnl ?? 0
    },

    /**
     * 账户级当日盈亏 —— 今日碰过的各交易所之和。
     *
     * 与 dailyPnlNet 的关系：后者只是当前这一家。单跑时两者相等；
     * 今天切换过交易所时，只有这个数才是账户真实的当日盈亏。
     */
    accountDailyPnl() {
      return this.rawAccountDailyPnl ?? 0
    },

    /**
     * 账户级数字能不能拿去判风险线。
     *
     * 今天碰过的某一家拿不到当日盈亏时为 false —— 那个合计是**残缺**的，
     * 而残缺的数比没有数更危险，因为它长得像个正常值。
     */
    accountPnlTrustworthy() {
      return this.rawAccountDailyPnl !== null && this.accountPnlComplete
    },

    /** 今天是否跨了交易所。跨了才需要在界面上强调账户级数字。 */
    multiExchangeToday() {
      return this.accountPnlParts.length > 1
    },

    pnlNote() {
      if (this.untrackedQty > 0) {
        return `尚有 ${this.untrackedQty} ${this.baseAsset} 无成本依据，请补全期初持仓声明`
      }
      if (this.rawUnrealized === null) return '缺少盘口或费率，净浮盈亏暂时算不出'
      if (this.rawDailyPnl === null) return '尚未捕获今日基线，当日盈亏暂不可用'
      if (!this.dayComplete) return '今日基线为进程中途捕获，当日盈亏只覆盖捕获之后的部分'
      return `账本已计入 ${this.fillsApplied} 笔成交`
    },

    /**
     * 净盈亏不可信时，该做什么才能修好。
     *
     * 原来告警条把补救建议写死成"去补期初持仓声明"。声明补完之后，
     * 卡点会换成"盘口没数据"，可提示还在让人去补一遍刚补过的东西 ——
     * 照着做没有任何效果，只会让人怀疑是不是自己填错了。
     * 补救建议必须跟着真实原因走。
     */
    pnlRemedy() {
      if (this.untrackedQty > 0) {
        return '请到「持仓 → 期初持仓声明」按真实建仓成本补全'
      }
      if (this.rawUnrealized === null) {
        return '这不是声明的问题：买盘为空时无法按可成交价估算浮盈，需等盘口恢复'
      }
      if (this.rawDailyPnl === null) {
        return '基线会在拿到有效总盈亏后自动捕获，无需人工操作'
      }
      return ''
    },

    // --- 系统 ---
    apiHealth: (s) => s.snap?.system?.api_health ?? 'OK',
    clockOffsetMs: (s) => s.snap?.system?.clock_offset_ms ?? 0,
    clockDriftMs: (s) => s.snap?.system?.clock_drift_ms ?? 0,
    clockRttMs: (s) => s.snap?.system?.clock_rtt_ms ?? 0,
    /** 时钟漂移容忍上限，由后端交易所配置下发 */
    maxClockSkewMs: (s) => s.snap?.system?.max_clock_skew_ms ?? 3000,
    reconStatus: (s) => s.snap?.system?.reconciliation_status ?? null,
    enableActions: (s) => !!s.snap?.system?.enable_actions,
    baseUrl: (s) => s.snap?.system?.base_url ?? '',
    environment: (s) => s.snap?.system?.environment ?? 'sandbox',
    exchange: (s) => s.snap?.system?.exchange ?? 'hashkey',
    exchangeLabel: (s) => s.snap?.system?.exchange_label ?? s.snap?.system?.exchange ?? '—',
    hasCredentials: (s) => !!s.snap?.system?.has_credentials,

    /** 当前环境的规则档案。阈值一律以后端为准，前端不再自留一份。 */
    profile: (s) => s.snap?.system?.profile ?? null,
    profileLabel: (s) => s.snap?.system?.profile?.label ?? '—',
    staleLimitMs: (s) => s.snap?.system?.profile?.stale_limit_ms ?? 60000,
    maxSpreadBps: (s) => num(s.snap?.system?.profile?.max_spread_bps) ?? 25,
    needsCalibration: (s) => s.snap?.system?.profile?.needs_calibration ?? [],
    /** 有活动挂单时切换会被后端拒绝，界面提前提示，不让人白点一次 */
    hasOpenOrders: (s) => (s.snap?.orders?.open_orders ?? []).some((o) => o.is_open),

    stale: (s) => s.connected && Date.now() - s.lastAt > 12000,
  },

  actions: {
    /** @param fresh 强制后端作废缓存重新取数（人工"重新检查"用） */
    async pull(fresh = false) {
      try {
        const snap = await api.snapshot(fresh)
        this.snap = snap
        this.connected = true
        this.lastAt = Date.now()
        this.error = ''

        const last = num(snap.market?.last)
        if (last !== null) {
          // 首次拿到真实价格时清掉占位点，否则图上会出现一段假的跳空
          if (!this.liveSeeded) {
            this.points = []
            this.liveSeeded = true
          }
          this.points.push(last)
          if (this.points.length > 240) this.points.shift()
        }
      } catch (e) {
        this.connected = false
        // 跨源被拦时浏览器只抛 "Failed to fetch"，不提 CORS —— 补一句提示，
        // 否则很容易误判成后端没起。
        this.error =
          String(e.message || e) +
          (api.isSameOrigin ? '' : `（后端 ${api.base}，跨源需后端放行本页来源）`)
      }
    },

    start(intervalMs = 2000) {
      if (this.polling) return
      this.pull()
      this.polling = setInterval(() => this.pull(), intervalMs)
    },

    stop() {
      if (this.polling) clearInterval(this.polling)
      this.polling = null
    },

    async declareOpening(qty, price, fee) {
      await api.declareOpening(qty, price, fee)
      await this.pull()
    },

    async cancelAll() {
      const res = await api.cancelAll()
      await this.pull()
      return res
    },

    closeoutPreview(qty, bps) {
      return api.closeoutPreview(qty, bps)
    },

    async closeoutSlice(qty, limitPrice, bps, hardFloor) {
      const res = await api.closeoutSlice(qty, limitPrice, bps, hardFloor)
      await this.pull(true) // 挂单与余额都变了，拿此刻的事实而不是缓存
      return res
    },

    async loadAccounts() {
      try {
        this.accounts = await api.accounts()
      } catch {
        this.accounts = null // 拉不到就不显示，不用猜出来的数字占位
      }
      return this.accounts
    },

    async loadExchanges() {
      try {
        const res = await api.exchanges()
        this.exchanges = res.items ?? []
      } catch {
        // 拉不到就保持空清单：切换器会显示为不可用，
        // 而不是拿一份猜出来的清单让人以为能切
        this.exchanges = []
      }
      return this.exchanges
    },

    /**
     * 切换交易所。
     *
     * 切换后行情历史必须清空 —— 那条曲线是上一家的价格，
     * 留着会和新交易所的价格接成一段根本不存在的跳空。
     */
    async switchExchange(name, symbol = null, force = false) {
      this.switching = true
      try {
        const res = await api.switchExchange(name, symbol, force)
        this.points = []
        this.liveSeeded = false
        await this.pull(true)
        await this.loadExchanges()
        return res
      } finally {
        this.switching = false
      }
    },
  },
})

/* 让 store 支持热更新，理由同 risk.js：
   缺这一行时改 store 只有整页刷新才生效，容易误判成改动没起作用。 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConsoleStore, import.meta.hot))
}
